// The individual's subscription (BL-10, BL-11). It is shown only when the
// platform charges individuals: GET /billing/subscription answers 404
// otherwise and the section stays hidden. Paying sends an M-Pesa prompt to the
// person's own phone; the receipts list the payments that arrived. With
// automatic renewal on, the prompt is sent a day before each period ends.
import { Badge, Button, Group, Loader, Paper, Stack, Switch, Table, Text, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useMe } from "../../shared/session/me";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";

type Subscription = Schemas["Subscription"];
type Payment = Schemas["Payment"];

export const SUBSCRIPTION_KEY = ["billing", "subscription"] as const;
const PAYMENTS_KEY = ["billing", "payments"] as const;

const STATUS_COLOUR: Record<Subscription["status"], string> = {
  trial: "blue",
  active: "green",
  past_due: "orange",
  expired: "red",
};
export const PAYMENT_COLOUR: Record<Payment["status"], string> = {
  confirmed: "green",
  unmatched: "yellow",
  reversed: "gray",
};

export function SubscriptionSection() {
  const subscription = useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: () => unwrap(api.GET("/billing/subscription")),
  });
  // Hidden while loading and whenever the platform has no individual subscriptions.
  if (!subscription.data) return null;
  return <SubscriptionView subscription={subscription.data} />;
}

function SubscriptionView({ subscription: s }: { subscription: Subscription }) {
  const { t } = useTranslation();
  const f = useFormat();
  const me = useMe();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const price = f.money(s.price_minor, s.currency);

  const pay = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    const { data, error } = await api.POST("/billing/payments/checkout");
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("subscription.prompt_sent", { phone: me.data?.masked_phone ?? "", amount: price }));
    void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
  };

  const when =
    s.status === "trial"
      ? t("subscription.trial_ends", { date: f.date(s.trial_ends_at) })
      : s.status === "active"
        ? t("subscription.renews", { date: f.date(s.current_period_end) })
        : t("subscription.ended", { date: f.date(s.current_period_end) });

  return (
    <Paper withBorder p="md" radius="md" data-testid="subscription-section">
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={2} size="h4">
            {t("subscription.title")}
          </Title>
          <Badge color={STATUS_COLOUR[s.status]} data-testid="subscription-status">
            {t(`subscription.status_${s.status}`)}
          </Badge>
        </Group>
        <Text size="sm" data-testid="subscription-when">
          {when}
        </Text>
        <Text size="sm" data-testid="subscription-price">
          {t("subscription.price", { price })}
        </Text>
        {notice && <Notice>{notice}</Notice>}
        <FormError message={error} />
        <Button w="fit-content" loading={busy} onClick={() => void pay()}>
          {t("subscription.pay")}
        </Button>
        <Text size="xs" c="dimmed">
          {t("subscription.pay_hint")}
        </Text>
        <AutoRenew subscription={s} />
        <Payments />
      </Stack>
    </Paper>
  );
}

function AutoRenew({ subscription: s }: { subscription: Subscription }) {
  const { t } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const on = s.payment_method === "mpesa" && s.auto_renew === true;

  const toggle = async (value: boolean) => {
    setError(null);
    setBusy(true);
    const { data, error } = await api.PUT("/billing/subscription/payment-method", {
      body: { method: value ? "mpesa" : "none", auto_renew: value },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    queryClient.setQueryData(SUBSCRIPTION_KEY, data);
  };

  return (
    <Stack gap="xs">
      <Switch
        label={t("subscription.auto_renew")}
        description={t("subscription.auto_renew_hint", { phone: me.data?.masked_phone ?? "" })}
        checked={on}
        disabled={busy}
        onChange={(e) => void toggle(e.currentTarget.checked)}
        data-testid="auto-renew"
      />
      <FormError message={error} />
    </Stack>
  );
}

function Payments() {
  const { t } = useTranslation();
  const f = useFormat();
  const payments = useQuery({ queryKey: PAYMENTS_KEY, queryFn: () => unwrap(api.GET("/billing/payments")) });
  const items = payments.data?.items ?? [];
  return (
    <Stack gap="xs">
      <Title order={3} size="h5">
        {t("subscription.payments_title")}
      </Title>
      {payments.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : payments.isError ? (
        <FormError message={errorMessage(payments.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="payments-empty">
          {t("subscription.payments_empty")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={420}>
          <Table verticalSpacing="xs" aria-label={t("subscription.payments_title")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("subscription.paid_at")}</Table.Th>
                <Table.Th ta="right">{t("billing.amount")}</Table.Th>
                <Table.Th>{t("subscription.receipt")}</Table.Th>
                <Table.Th>{t("subscription.payment_status")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((p) => (
                <Table.Tr key={p.id} data-testid="payment-row">
                  <Table.Td>{f.dateTime(p.paid_at)}</Table.Td>
                  <Table.Td ta="right">{f.money(p.amount_minor, p.currency)}</Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {p.provider_ref}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={PAYMENT_COLOUR[p.status]} variant="light">
                      {t(`payment_status.${p.status}`)}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
